import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import api from '../services/api';

const AdminStores = () => {
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [storeUsers, setStoreUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [linkingStore, setLinkingStore] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
  });

  useEffect(() => {
    loadStores();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      // Filtrar lojistas e moderadores que não estão vinculados a uma loja
      const availableUsers = response.data.filter(user => 
        (user.role === 'merchant' || user.role === 'store_moderator') && !user.store_id
      );
      setUsers(availableUsers);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadStores = async () => {
    try {
      const response = await api.get('/stores');
      setStores(response.data);
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
    }
  };

  const handleOpenDialog = (store = null) => {
    if (store) {
      setEditingStore(store);
      setFormData({
        name: store.name || '',
        description: store.description || '',
        image_url: store.image_url || '',
        address: store.address || '',
        city: store.city || '',
        state: store.state || '',
        zip_code: store.zip_code || '',
        phone: store.phone || '',
        email: store.email || '',
        website: store.website || '',
      });
    } else {
      setEditingStore(null);
      setFormData({
        name: '',
        description: '',
        image_url: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        email: '',
        website: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStore(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingStore) {
        await api.put(`/stores/${editingStore.id}`, formData);
      } else {
        await api.post('/stores', formData);
      }
      handleCloseDialog();
      loadStores();
    } catch (error) {
      console.error('Erro ao salvar loja:', error);
      alert('Erro ao salvar loja');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar esta loja?')) {
      try {
        await api.delete(`/stores/${id}`);
        loadStores();
      } catch (error) {
        console.error('Erro ao deletar loja:', error);
        alert('Erro ao deletar loja');
      }
    }
  };

  const handleOpenLinkDialog = (store) => {
    setLinkingStore(store);
    setSelectedUserId('');
    setOpenLinkDialog(true);
  };

  const handleOpenUsersDialog = async (store) => {
    setSelectedStore(store);
    setOpenUsersDialog(true);
    await loadStoreUsers(store.id);
  };

  const handleCloseUsersDialog = () => {
    setOpenUsersDialog(false);
    setSelectedStore(null);
    setStoreUsers([]);
  };

  const loadStoreUsers = async (storeId) => {
    try {
      const response = await api.get(`/stores/${storeId}/users`);
      setStoreUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários da loja:', error);
    }
  };

  const handleToggleModerator = async (userId, isModerator) => {
    try {
      await api.put(`/stores/${selectedStore.id}/users/${userId}/moderator`, {
        is_moderator: !isModerator
      });
      await loadStoreUsers(selectedStore.id);
      loadUsers(); // Recarregar lista de usuários disponíveis
      alert(isModerator ? 'Moderador removido com sucesso!' : 'Moderador definido com sucesso!');
    } catch (error) {
      console.error('Erro ao alterar status de moderador:', error);
      alert(error.response?.data?.error || 'Erro ao alterar status de moderador');
    }
  };

  const handleUnlinkUser = async (userId, userName) => {
    if (!window.confirm(`Tem certeza que deseja remover ${userName} desta loja?`)) {
      return;
    }

    try {
      await api.delete(`/stores/${selectedStore.id}/users/${userId}`);
      alert('Usuário removido da loja com sucesso!');
      await loadStoreUsers(selectedStore.id);
      loadUsers(); // Recarregar lista de usuários disponíveis
    } catch (error) {
      console.error('Erro ao remover usuário da loja:', error);
      alert(error.response?.data?.error || 'Erro ao remover usuário da loja');
    }
  };

  const handleCloseLinkDialog = () => {
    setOpenLinkDialog(false);
    setLinkingStore(null);
    setSelectedUserId('');
  };

  const handleLinkUser = async () => {
    if (!selectedUserId) {
      alert('Selecione um usuário');
      return;
    }

    const storeId = linkingStore?.id || selectedStore?.id;
    if (!storeId) {
      alert('Erro: Loja não selecionada');
      return;
    }

    try {
      await api.post(`/stores/${storeId}/users`, {
        user_id: parseInt(selectedUserId)
      });
      alert('Usuário vinculado à loja com sucesso!');
      if (linkingStore) {
        handleCloseLinkDialog();
      } else if (selectedStore) {
        setSelectedUserId('');
        await loadStoreUsers(selectedStore.id);
      }
      loadStores();
      loadUsers(); // Recarregar lista de usuários disponíveis
    } catch (error) {
      console.error('Erro ao vincular usuário:', error);
      alert(error.response?.data?.error || 'Erro ao vincular usuário');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, pt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Gerenciar Lojas
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nova Loja
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Cidade</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Produtos</TableCell>
              <TableCell align="right">Score Total</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stores.map((store) => (
              <TableRow key={store.id}>
                <TableCell>{store.name}</TableCell>
                <TableCell>{store.city}</TableCell>
                <TableCell>{store.state}</TableCell>
                <TableCell align="right">{store.products_count || 0}</TableCell>
                <TableCell align="right">
                  {(Number(store.total_engagement) || 0).toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenUsersDialog(store)}
                    title="Gerenciar usuários da loja"
                    sx={{ color: 'primary.main' }}
                  >
                    <PeopleIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(store)}
                    title="Editar"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(store.id)}
                    title="Deletar"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingStore ? 'Editar Loja' : 'Nova Loja'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label="URL da Imagem"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Endereço"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Cidade"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Estado"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="CEP"
            value={formData.zip_code}
            onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Telefone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.name || !formData.address || !formData.city || !formData.state}
          >
            {editingStore ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openLinkDialog} onClose={handleCloseLinkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Vincular Usuário à Loja: {linkingStore?.name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Selecione um Lojista ou Moderador</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Selecione um Lojista ou Moderador"
            >
              {users.length === 0 ? (
                <MenuItem disabled>Nenhum lojista ou moderador disponível</MenuItem>
              ) : (
                users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} ({user.email}) - {user.role === 'store_moderator' ? 'Moderador' : 'Lojista'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {users.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Você precisa criar um usuário com perfil "Lojista" ou "Moderador da Loja" primeiro.
              Acesse a página de Usuários para criar. Usuários já vinculados a outras lojas não aparecem aqui.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog}>Cancelar</Button>
          <Button
            onClick={handleLinkUser}
            variant="contained"
            color="primary"
            disabled={!selectedUserId || users.length === 0}
          >
            Vincular
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openUsersDialog} onClose={handleCloseUsersDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Gerenciar Usuários - {selectedStore?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Usuários Vinculados
            </Typography>
            {storeUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                Nenhum usuário vinculado a esta loja ainda.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Perfil</TableCell>
                      <TableCell align="center">Moderador</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {storeUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.role === 'store_moderator' ? 'Moderador' : 'Lojista'}
                            size="small"
                            color={user.role === 'store_moderator' ? 'secondary' : 'warning'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={user.is_moderator === 1}
                                onChange={() => handleToggleModerator(user.id, user.is_moderator === 1)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: 'primary.main',
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: 'primary.main',
                                  },
                                }}
                              />
                            }
                            label={user.is_moderator === 1 ? 'Sim' : 'Não'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleUnlinkUser(user.id, user.name)}
                            title="Remover da loja"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Vincular Novo Usuário
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Selecione um Lojista ou Moderador</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label="Selecione um Lojista ou Moderador"
              >
                {users.length === 0 ? (
                  <MenuItem disabled>Nenhum usuário disponível</MenuItem>
                ) : (
                  users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email}) - {user.role === 'store_moderator' ? 'Moderador' : 'Lojista'}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {users.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Todos os lojistas e moderadores já estão vinculados a lojas. Crie novos usuários na página de Usuários.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUsersDialog}>Fechar</Button>
          <Button
            onClick={async () => {
              await handleLinkUser();
              if (selectedStore && selectedUserId) {
                setSelectedUserId('');
                await loadStoreUsers(selectedStore.id);
              }
            }}
            variant="contained"
            color="primary"
            disabled={!selectedUserId || users.length === 0}
          >
            Vincular
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminStores;
